<template lang="html">
  <div>
    <!-- MEMBER PREFERENCES DIALOG -->
    <member-preferences
      v-model="selectedMemberPreferencesId"
    />
    <!-- REPORT BUILDER -->
    <report-builder
      v-model="reportFilters"
      title="Cancellations Report"
      :sub-title="subTitle"
      type="cancellations"
      command="diningReservationDetailReport"
      saved-report-command="diningSavedReport"
      :command-filter="commandFilter"
      :resources="reportFilterResourceCommands"
      :defaults="defaultReportFilters"
      :headers="headers"
      :report-fields="includeFields"
      :report-filters="currentFilterSelection"
      :report-order="sortOrder"
      :report-grouping="groupOrder"
      :report-grouping-fields="getGroupFields"
      :before-run="beforeRun"
      :grouped="isGrouped"
      show-summary
      summary-caption="Summary"
    >
      <!-- REPORT FORM -->
      <!--  DATE SEARCH TYPE SELECT  -->
      <template v-slot:filter-type>
        <v-select
          v-model="reportFilters.dateSearchType"
          prepend-icon="mdi-cog-box"
          :items="searchOptions"
          item-text="text"
          item-value="value"
          label="Search Type"
          clearable
        />
      </template>

      <!--  EVENT FILTER  -->
      <template v-slot:filter-event>
        <v-autocomplete
          v-if="eventsOnly"
          v-model="reportFilters.eventNames"
          prepend-icon="mdi-calendar"
          label="Event"
          :items="groupedSelectableEvents"
          multiple
          item-text="name"
          item-value="name"
          chips
          clearable
        >
          <template
            slot="selection"
            slot-scope="data"
          >
            <v-chip
              :key="JSON.stringify(data.item)"
              :input-value="data.selected"
              class="chip--select-multi"
              @input="data.parent.selectItem(data.item)"
              @click="data.parent.selectItem(data.item)"
            >
              {{ data.item.name }}
            </v-chip>
          </template>
        </v-autocomplete>
      </template>

      <!--  DATE  -->
      <template v-slot:filter-date>
        <v-autocomplete
          v-if="eventsOnly && reportFilters.eventNames && reportFilters.eventNames[0]"
          v-model="reportFilters.dates"
          label="Date"
          prepend-icon="mdi-calendar"
          :items="groupedEventsDateList"
          multiple
          chips
          clearable
        >
          <template
            slot="selection"
            slot-scope="data"
          >
            <v-chip
              :key="JSON.stringify(data.item)"
              :input-value="data.selected"
              class="chip--select-multi"
              @input="data.parent.selectItem(data.item)"
              @click="data.parent.selectItem(data.item)"
            >
              {{ data.item.text }}
            </v-chip>
          </template>
        </v-autocomplete>
        <v-layout v-else>
          <v-flex class="xs6">
            <date-picker
              v-model="reportFilters.date"
              label="Start Date"
            />
          </v-flex>
          <v-flex class="xs6 pl-1">
            <!--  OPTIONAL END DATE  -->
            <date-picker
              v-model="reportFilters.endDate"
              :icon="null"
              label="End Date"
              clearable
            />
          </v-flex>
        </v-layout>
      </template>

      <!--  LOCATION FILTER -->
      <template v-slot:filter-location>
        <v-autocomplete
          v-model="reportFilters.locationIds"
          prepend-icon="mdi-map-marker"
          label="Location"
          :items="filteredLocations"
          multiple
          item-text="name"
          item-value="id"
          chips
          clearable
        >
          <template
            slot="selection"
            slot-scope="data"
          >
            <v-chip
              :key="JSON.stringify(data.item)"
              :input-value="data.selected"
              class="chip--select-multi"
              @input="data.parent.selectItem(data.item)"
              @click="data.parent.selectItem(data.item)"
            >
              {{ data.item.name }}
            </v-chip>
          </template>
        </v-autocomplete>
      </template>

      <!--  REPORT COLUMNS  -->
      <template v-slot:filter-columns-lg>
        <v-autocomplete
          v-model="reportFilters.columns"
          prepend-icon="mdi-view-list"
          label="Columns"
          :items="columnOptions.filter(c => c.optional)"
          multiple
          item-text="name"
          item-value="id"
          chips
          clearable
        >
          <template
            slot="selection"
            slot-scope="data"
          >
            <v-chip
              :key="JSON.stringify(data.item)"
              :input-value="data.selected"
              class="chip--select-multi"
              @input="data.parent.selectItem(data.item)"
              @click="data.parent.selectItem(data.item)"
            >
              {{ data.item.name }}
            </v-chip>
          </template>
        </v-autocomplete>
      </template>

      <!--  REPORT DETAIL DISPLAY  -->
      <template v-slot:filter-detail-grouping>
        <v-autocomplete
          v-model="reportFilters.detailGrouping"
          prepend-icon="mdi-account-group"
          label="Reservation Display"
          :items="detailGroupingOptions"
          item-text="name"
          item-value="id"
        />
      </template>

      <!--  REPORT GROUPING -->
      <template v-slot:filter-grouping>
        <v-autocomplete
          v-model="reportFilters.grouping"
          prepend-icon="mdi-sort"
          label="Grouping"
          :items="groupingOptions"
          item-text="name"
          item-value="id"
          clearable
        />
      </template>

      <!--  REPORT SORT ORDER -->
      <template v-slot:filter-order>
        <v-autocomplete
          v-model="reportFilters.sortBy"
          prepend-icon="mdi-sort"
          label="Sort Order"
          :items="sortOptions"
          item-text="name"
          item-value="id"
        />
      </template>

      <!-- FILTER BY MEMBER -->
      <template v-slot:filter-member>
        <member-select
          v-model="reportFilters.memberId"
          prepend-icon="mdi-account"
          label="Member Search"
          :error-handler="apiResourceError"
          enable-info-select
          @info-select="openMemberPreferences"
        />
      </template>

      <!--  SHOW / HIDE VIOLATORS ONLY  -->
      <template v-slot:filter-violators>
        <v-boolean
          v-model="reportFilters.onlyViolations"
          label="Only Show Billable"
          hide-details
        />
      </template>

      <!-- REPORT HEADERS -->
      <!-- (No header slot overrides needed) -->

      <!-- REPORT ROWS/COLUMNS -->
      <template
        slot="td-names"
        slot-scope="props"
      >
        <span
          v-for="(name, i) in props.header.displayCell(props.row, props.index, props.rows)"
          :key="i"
          class="ft-report-reservee"
        >
          <span
            v-if="name.guestType && !isNonDiscreteRoot(props.row)"
            :class="{'ft-report-reservee-gt': true, 'ft-has-name': !!name.name}"
          >{{ name.guestType }}</span>
          <span
            v-if="name.name"
            class="ft-report-reservee-name"
          >{{ name.name }}</span>
          <span
            v-if="name.count > 1"
            class="ft-report-reservee-count"
          >{{ name.count }}</span>
        </span>
      </template>
      <!-- <template slot="td-cancelFeeExempt" slot-scope="props">{{ (props.item === null ? 'Exempt' : 'Billable') }}</template> -->

      <!-- REPORT SUMMARY -->
      <template slot="summary-body">
        <div>Total Reservees: {{ reserveeCount }}</div>
        <div>Total Reservations: {{ reservationCount }}</div>
        <div v-if="eventCostCounts[0]">
          Event Price Breakdown
        </div>
        <div
          v-for="(eventCost, ei) in sortedEventCountCosts"
          :key="ei"
          class="ft-report-summary-event-cost"
        >
          {{ eventCost.name }}: {{ eventCost.total }}
        </div>
      </template>
    </report-builder>
  </div>
</template>

<script>
import {helpers, errorHandler, rules, groupableReport} from '@/ft-vue-core'
import detailCommon from '../common/mixins/detailCommon'
import options from './mixins/options'

const {deepClone, verboseDate} = helpers

const defaultReportFilters = {
  scale: 1,
  eventIds: [],
  eventNames: [],
  locationIds: [],
  sortBy: 1,
  grouping: 1,
  detailGrouping: 1,
  columns: [1, 2, 3, 4, 30, 14, 15, 24, 36, 37, 38, 39, 40],
  date: null,
  endDate: null,
  dates: [],
  paperSize: null, // Defaults to LETTER
  orientation: null, // Defaults to PORTRAIT
  email: null,
  dateSearchType: null,
  onlyViolations: null,
  memberId: null
}

export default {
  name: 'CancellationsReport',
  mixins: [rules, errorHandler, groupableReport, detailCommon, options],
  data: function () {
    return {
      defaultReportFilters: deepClone(defaultReportFilters),
      reportFilters: deepClone(defaultReportFilters),
      searchOptions: [
        {text: 'Date Range Excluding All Events', value: 'rangeNoEvents'},
        {text: 'Date Range Including All Events', value: 'rangeWithEvents'},
        {text: 'By Event', value: 'event'}
      ],
      events: [],
      eventCostCounts: [],
      selectedMemberPreferencesId: null
    }
  },
  computed: {
    eventsOnly () {
      return this.reportFilters.dateSearchType === 'event'
    },
    sortedEventCountCosts () {
      // Sort by cost name, pushing 'None' to the bottom
      return this.eventCostCounts.slice().sort((a, b) => a.id ? a.name.toLowerCase().localeCompare(b.name.toLowerCase()) : 1)
    },
    subTitle () {
      return this.selectedDetailGrouping.subTitle + (this.reportFilters.date ? ' - ' + verboseDate(this.reportFilters.date) : '')
    },
    eventMap () {
      return this.events.reduce((result, item) => {
        result[item.id] = item
        return result
      }, {})
    },
    eventCostMap () {
      return this.eventCosts.reduce((result, item) => {
        result[item.id] = item
        return result
      }, {})
    },
    detailGroupingMap () {
      return this.detailGroupingOptions.reduce((result, item) => {
        result[item.id] = item
        return result
      }, {})
    },
    selectedDetailGrouping () {
      return this.detailGroupingMap[this.reportFilters.detailGrouping] || {}
    },
    /* overrides super */
    selectedGroupingFilters () {
      return this.selectedGrouping.concat(this.selectedDetailGrouping.group || [])
    },
    groupedByReservation () {
      return this.selectedDetailGrouping.group && this.selectedDetailGrouping.group[0] || this.selectedDetailGrouping.consolidated === true
    }
  },
  /**
   * TODO: Move any methods than don't require access to "this" to a helpers include
   *       There is overhead in calling this.xxx methods
   */
  methods: {
    openMemberPreferences (member) {
      if (!member || !member.id) return
      this.selectedMemberPreferencesId = member.id
    },
    beforeRun (f) {
      this.subGroupHighlight = true
    },
    getExtraIncludeFields () {
      // Add any fields needed by the selected detail grouping option
      return this.getGroupFields(this.selectedDetailGrouping)
      // .concat(['eventCostId', 'eventCostName', 'confirmationStatusCode'])
    },
    reservationChanged (row, rowIndex, rows) {
      return !this.groupedByReservation || (rowIndex === 0 || row.reservationId !== rows[rowIndex - 1].reservationId)
    },
    skipRow (row, rowIndex) {
      // Skip any non-discreteCovers.  (probably should move this to the query filter)
      // return !row.discreteCovers && row.pos > 0 && !row.memberId
      return false
    },
    extraSortOrder (f) {
      // Groupable mixin sorts by grouped order first, then we add any other sorting
      return (this.sortOptionMap[f.sortBy] || {}).order || []
    },
    currentFilterSelection (f) {
      var filters = []
      var reservationFilters = []
      var hasEvents = false
      switch (f.dateSearchType) {
      case 'event':
        // EVENTS
        if (f.eventNames && f.eventNames.length) {
          const eventIds = this.getSelectedEventIds(f)
          reservationFilters.push({field: 'eventId', operator: 'IN', values: eventIds})
          const dateFilters = this.getSelectedDatesFilter(f)
          if (dateFilters.length) reservationFilters.push({filter: dateFilters})
          hasEvents = true
        } else {
          // Only events
          reservationFilters.push({field: 'eventId', operator: 'IS_NOT_NULL'})
        }
        break
      case 'rangeNoEvents':
        // Exclude events
        reservationFilters.push({field: 'eventId', operator: 'IS_NULL'})
        /* falls through */
      case 'rangeWithEvents':
        // if (!f.dateSearchType === 'rangeNoEvents') reservationFilters.push({field: 'eventId', operator: 'IS_NOT_NULL'})
        /* falls through */
      default:
        // Range
      }

      if (!hasEvents) {
        if (f.date && f.endDate) {
          reservationFilters.push({
            field: 'dateTime',
            operator: 'BETWEEN',
            values: [
              f.date + ' 00:00:00',
              f.endDate + ' 23:59:59'
            ]
          })
        } else if (f.date) {
          reservationFilters.push({
            field: 'dateTime',
            operator: 'BETWEEN',
            values: [
              f.date + ' 00:00:00',
              f.date + ' 23:59:59'
            ]
          })
        }
      }

      // LOCATIONS
      if (f.locationIds && f.locationIds.length) {
        reservationFilters.push({field: 'locationId', operator: 'IN', values: f.locationIds})
      }
      // RESERVATION SUB FILTER
      if (reservationFilters.length) {
        filters.push({
          field: 'reservationId',
          operator: 'IN',
          subQueryTarget: 'diningReservation.id',
          subQueryFilter: reservationFilters
        })
      }
      // ONLY INCLUDE CANCELED RESERVATIONS
      filters.push({field: 'cancelled', operator: 'IS_NOT_NULL'})
      // EXCLUDE FEE EXEMPT (ONLY SHOW BILLABLE CANCELLATIONS)
      if (f.onlyViolations) {
        filters.push({field: 'cancelFeeExempt', operator: 'NULL_SAFE_NOT_EQUAL', values: [1]})
        filters.push({field: 'cancellationChargeApplies', operator: 'EQUAL', values: [1]})
      }
      if (f.memberId) {
        const memberIdFilters = []
        memberIdFilters.push({field: 'memberId', operator: 'EQUAL', values: [f.memberId]})
        memberIdFilters.push({condition: 'OR', field: 'guestHostId', operator: 'EQUAL', values: [f.memberId]})
        filters.push({filter: memberIdFilters})
      }
      console.log('Selected Filters', filters)
      return filters
    },
    validFilters () {
      return true
    },
    reportFilterResourceCommands () {
      return [
        {command: 'diningLocation', load: this.locations},
        {command: 'diningEvent', load: this.events, params: {include_fields: ['locations']}}
      ]
    }
  }
}
</script>

<style>
  td .ft-report-reservee {
    white-space: nowrap;
    display: inline-block;
  }
  td .ft-report-reservee .ft-report-reservee-gt.ft-has-name::after {
    content: ": ";
  }
  td .ft-report-reservee .ft-report-reservee-count::before {
    content: " (";
  }
  td .ft-report-reservee .ft-report-reservee-count::after {
    content: ")";
  }
  td .ft-report-reservee:nth-last-child(n+2)::after {
    content: "; ";
  }
  .ft-report-summary-event-cost {
    margin-left: 10px
  }
</style>
